/*
 App.tsx
 =========

------------------------------------------------------------------------
 Author:   Oskar Truffer                                        2025
 Modifs:

 Goal:   Demonstrator for FUOTA status tracking & management for TRACE.

   (c) 2025  Oskar Truffer   
   --------------------------

   CSEM S.A.
   Jaquet-Droz 1
   CH-2000 Neuchâtel
   http://www.csem.ch


   THIS SOFTWARE IS CONFIDENTIAL AND CANNOT BE DISTRIBUTED
   WITHOUT THE CSEM PRIOR WRITTEN AGREEMENT.

   THIS  SOFTWARE CAN ONLY BE USED IN THE EUROPEAN PROJECT TRACE AND
   SHALL REMAIN PRIVATE AT ALL TIMES.

------------------------------------------------------------------------
*/

import { useCallback, useEffect, useState } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import awsConfig from './aws-config.json'
import './App.scss';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import { Login } from './components/Login';
import { Frame } from './pages/Frame';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import ErrorPage from './pages/ErrorPage';

import 'katex/dist/katex.min.css'
import OTAExamplePage from './pages/OTAExamplePage';
import { Lambda } from '@aws-sdk/client-lambda';
import { useInjection } from 'inversify-react';
import { Credentials } from './services/Credentials';
import awsResources from './aws-resources.json'

export default function App() {
  const credentials = useInjection(Credentials);
  
  
  const scheme = window.location.href.split("://")[0];
  const host_and_port = window.location.href.split("://")[1].split("/")[0];
  const base_url = `${scheme}://${host_and_port}/`
  
  awsConfig.oauth.redirectSignIn = base_url;
  awsConfig.oauth.redirectSignOut = base_url;
  
  Amplify.configure(awsConfig);
  
  const [user, setUser] = useState<any | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userAttributes, setUserAttributes] = useState<Map<string, string> | null>(null);


  const attachIoTPolicy = useCallback(async () => {        
    
    const lambdaClient = new Lambda({
      region: awsConfig.aws_cognito_region,
      credentials: await credentials.getProvider()
    });

    return new TextDecoder().decode((await lambdaClient.invoke({
      FunctionName: awsResources.IoTAttachPrincipalLambdaName,

    })).Payload);
  }, [credentials]);

  const loadAuthentication = useCallback(async () => {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: false })
      .catch((err) => {
        setAuthLoading(false);
        setUser(null);
        setUserAttributes(null);

        // If we have anything else than just a simple non-authenticated issue, we rethrow.
        if (err !== "The user is not authenticated") {
          throw err;
        }
      });

    if (!user)
      return;

    const attrL = await Auth.userAttributes(user);
    var attr: Map<string, string> = new Map();
    attrL.forEach(i => attr.set(i.getName(), i.getValue()));
    
    setAuthLoading(false);
    setUserAttributes(attr);
    setUser(user);

    if (user) {
      attachIoTPolicy()
    }
    // console.log(attr);
    return () => {
      // Nothing to cancel.
    }
  }, [attachIoTPolicy]);

  useEffect(() => {
    loadAuthentication().catch(console.error);
  }, [loadAuthentication]);


  return (
    <>
      <script src="https://cdn.jsdelivr.net/npm/react/umd/react.production.min.js"></script>

      {user != null && (
        <BrowserRouter>
          <Routes >
            <Route path="/" element={<OTAExamplePage onLogout={() => {
              loadAuthentication();
            }
            }/>} />
              <Route path='ota-example' element={<OTAExamplePage onLogout={() => {
              loadAuthentication();
            }
            }/>} />
              <Route path="*" element={<>No such page.</>} >
            </Route>
          </Routes>
        </BrowserRouter>
      ) || (<Login authLoading={authLoading} onSuccessfulAuthentication={() => {
        loadAuthentication();
      }}></Login>)}

    </>
  );
}

